import { useState, useEffect, useCallback } from 'react';
import { requisitesService, tradersService, dictionariesService } from '../../../services';
import { Requisite, RequisiteFormData } from '../../../types/requisite';
import { Trader } from '../../../types/trader';
import { Currency } from '../../../types/common/currency';
import { PaymentSystem, Bank } from '../../../types/common/payment';
import { useNotification } from '../../../contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import { REQUISITES_MAX_LIMIT, type GetRequisitesParams } from '@/types/requisite';

export interface RequisiteFiltersState {
  name_or_number: string;
  trader_id: string;
  payment_system_id: string;
  is_active: '' | 'true' | 'false';
  is_blocked_by_trader: '' | 'true' | 'false';
  include_pre_deleted: boolean;
  transaction_limit_sort: '' | 'asc' | 'desc';
  page: number;
  per_page: number;
}

const DEFAULT_PER_PAGE = 20;
const MAX_PER_PAGE = REQUISITES_MAX_LIMIT;

const defaultFilters: RequisiteFiltersState = {
  name_or_number: '',
  trader_id: '',
  payment_system_id: '',
  is_active: '',
  is_blocked_by_trader: '',
  include_pre_deleted: false,
  transaction_limit_sort: '',
  page: 1,
  per_page: DEFAULT_PER_PAGE,
};

function buildRequisitesQuery(filters: RequisiteFiltersState): GetRequisitesParams {
  const page = filters.page ?? 1;
  const perPage = Math.min(filters.per_page ?? DEFAULT_PER_PAGE, MAX_PER_PAGE);
  const params: GetRequisitesParams = {
    skip: (page - 1) * perPage,
    limit: perPage,
    include_pre_deleted: filters.include_pre_deleted,
  };

  const nameOrNumber = filters.name_or_number.trim();
  const traderId = filters.trader_id.trim();
  const paymentSystemId = filters.payment_system_id.trim();

  if (nameOrNumber) {
    params.name_or_number = nameOrNumber;
  }
  if (traderId) {
    params.trader_id = traderId;
  }
  if (paymentSystemId) {
    params.payment_system_id = paymentSystemId;
  }
  if (filters.is_active) {
    params.is_active = filters.is_active === 'true';
  }
  if (filters.is_blocked_by_trader) {
    params.is_blocked_by_trader = filters.is_blocked_by_trader === 'true';
  }
  if (filters.transaction_limit_sort) {
    params.sort_by = 'transaction_limit';
    params.sort_order = filters.transaction_limit_sort;
  }

  return params;
}

export function useRequisites() {
  const { showNotification } = useNotification();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [requisites, setRequisites] = useState<Requisite[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [paymentSystems, setPaymentSystems] = useState<PaymentSystem[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<RequisiteFiltersState>(defaultFilters);
  const [open, setOpen] = useState(false);
  const [editingRequisite, setEditingRequisite] = useState<Requisite | null>(null);
  const [formData, setFormData] = useState<RequisiteFormData>({
    name: '',
    trader_id: '',
    payment_system_id: '',
    currency_id: 0,
    number: '',
    second_number: '',
    holder_name: '',
    bank_id: '',
    min_transaction_limit: 0,
    max_transaction_limit: 0,
    counter_limit: 0,
    amount_limit: 0,
    current_counter: 0,
    current_amount: 0,
    pending_amount: 0,
    pending_counter: 0,
    is_active: true,
    pre_deled: false,
  });

  const loadReferenceData = useCallback(async () => {
    try {
      const [tradersData, currenciesData, paymentSystemsData, banksData] = await Promise.all([
        tradersService.getTraders({ is_archive: false }),
        dictionariesService.getCurrencies(),
        dictionariesService.getPaymentSystems(),
        dictionariesService.getBanks(),
      ]);
      setTraders(tradersData);
      setCurrencies(currenciesData);
      setPaymentSystems(paymentSystemsData);
      setBanks(banksData);
    } catch (err) {
      console.error('Error loading reference data:', err);
      showNotification('Ошибка при загрузке справочников', 'error');
    }
  }, [showNotification]);

  const loadRequisites = useCallback(
    async (custom?: RequisiteFiltersState) => {
      let request = custom ?? filters;

      setLoading(true);
      try {
        let response = await requisitesService.getRequisites(buildRequisitesQuery(request));
        const page = request.page ?? 1;
        const serverTotalPages = Math.max(1, response.total_pages);

        if (page > serverTotalPages && response.total_pages > 0) {
          request = { ...request, page: serverTotalPages };
          setFilters(request);
          response = await requisitesService.getRequisites(buildRequisitesQuery(request));
        } else if (response.items.length === 0 && page > 1) {
          request = { ...request, page: page - 1 };
          setFilters(request);
          response = await requisitesService.getRequisites(buildRequisitesQuery(request));
        }

        setRequisites(response.items);
        setTotalPages(Math.max(1, response.total_pages));
        setError(null);
      } catch (err) {
        console.error('Error loading requisites:', err);
        setError('Ошибка при загрузке реквизитов');
        setRequisites([]);
        showNotification('Ошибка при загрузке реквизитов', 'error');
      } finally {
        setLoading(false);
      }
    },
    [filters, showNotification],
  );

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadRequisites();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- начальная загрузка
  }, []);

  const handleOpen = (requisite?: Requisite) => {
    if (requisite) {
      if (!canPerformAction(currentUser, ActionKey.REQUISITES_UPDATE)) {
        showNotification('У вас нет прав для редактирования реквизитов', 'error');
        return;
      }
      setEditingRequisite(requisite);
      setFormData({
        name: requisite.name,
        trader_id: requisite.trader_id,
        payment_system_id: requisite.payment_system_id,
        currency_id: 0,
        number: requisite.number,
        second_number: requisite.second_number,
        holder_name: requisite.holder_name,
        bank_id: requisite.bank_id,
        min_transaction_limit: requisite.min_transaction_limit,
        max_transaction_limit: requisite.max_transaction_limit,
        counter_limit: requisite.counter_limit,
        amount_limit: requisite.amount_limit,
        current_counter: requisite.current_counter,
        current_amount: requisite.current_amount,
        pending_amount: requisite.pending_amount,
        pending_counter: requisite.pending_counter,
        is_active: requisite.is_active,
        pre_deled: requisite.pre_deled,
      });
    } else {
      if (!canPerformAction(currentUser, ActionKey.REQUISITES_CREATE)) {
        showNotification('У вас нет прав для создания реквизитов', 'error');
        return;
      }
      setEditingRequisite(null);
      setFormData({
        name: '',
        trader_id: '',
        payment_system_id: '',
        currency_id: 0,
        number: '',
        second_number: '',
        holder_name: '',
        bank_id: '',
        min_transaction_limit: 0,
        max_transaction_limit: 0,
        counter_limit: 0,
        amount_limit: 0,
        current_counter: 0,
        current_amount: 0,
        pending_amount: 0,
        pending_counter: 0,
        is_active: true,
        pre_deled: false,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingRequisite(null);
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    const hasPermission = editingRequisite
      ? canPerformAction(currentUser, ActionKey.REQUISITES_UPDATE)
      : canPerformAction(currentUser, ActionKey.REQUISITES_CREATE);

    if (!hasPermission) {
      const action = editingRequisite ? 'редактирования' : 'создания';
      showNotification(`У вас нет прав для ${action} реквизитов`, 'error');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      const submitData: Omit<
        Requisite,
        'id' | 'created_at' | 'updated_at' | 'is_blocked_by_trader'
      > = {
        name: formData.name,
        trader_id: formData.trader_id,
        payment_system_id: formData.payment_system_id,
        number: formData.number,
        second_number: formData.second_number,
        holder_name: formData.holder_name,
        bank_id: formData.bank_id,
        min_transaction_limit: formData.min_transaction_limit,
        max_transaction_limit: formData.max_transaction_limit,
        counter_limit: formData.counter_limit,
        amount_limit: formData.amount_limit,
        current_counter: formData.current_counter,
        current_amount: formData.current_amount,
        pending_amount: formData.pending_amount,
        pending_counter: formData.pending_counter,
        is_active: formData.is_active,
        pre_deled: formData.pre_deled,
      };

      if (editingRequisite) {
        await requisitesService.updateRequisite(editingRequisite.id, submitData);
        setSuccess('Реквизиты успешно обновлены');
      } else {
        await requisitesService.createRequisite(submitData);
        setSuccess('Реквизиты успешно созданы');
      }
      await loadRequisites(filters);
      handleClose();
    } catch (err: any) {
      console.error('Error saving requisite:', err);
      const errorMessage =
        err.response?.data?.detail || err.message || 'Ошибка при сохранении реквизитов';
      setError(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    if (!canPerformAction(currentUser, ActionKey.REQUISITES_DELETE)) {
      showNotification('У вас нет прав для удаления реквизитов', 'error');
      return;
    }
    try {
      await requisitesService.deleteRequisite(id);
      await loadRequisites(filters);
      setSuccess('Реквизиты успешно удалены');
    } catch (err) {
      console.error('Error deleting requisite:', err);
      setError('Ошибка при удалении реквизитов');
    }
  };

  const handleToggleActive = async (id: string, newState: boolean) => {
    if (!canPerformAction(currentUser, ActionKey.REQUISITES_TOGGLE_ACTIVE)) {
      showNotification('У вас нет прав для изменения статуса реквизитов', 'error');
      return;
    }
    try {
      const updatedRequisite = await requisitesService.toggleRequisiteActive(id);
      await loadRequisites(filters);
      setSuccess(`Реквизиты ${updatedRequisite?.is_active ? 'активированы' : 'деактивированы'}`);
    } catch (err: any) {
      console.error('Error updating requisite status:', err);
      const status = err?.response?.status;
      if (status === 409 && newState) {
        showNotification(
          err?.response?.data?.detail || 'Невозможно активировать реквизит',
          'error',
        );
      } else {
        setError('Ошибка при обновлении статуса реквизитов');
      }
    }
  };

  const handleUnblock = async (id: string) => {
    try {
      const updatedRequisite = await requisitesService.unblockRequisite(id);
      await loadRequisites(filters);
      showNotification(
        `UPI-блокировка снята, реквизит ${updatedRequisite?.is_active ? 'активирован' : 'разблокирован'}`,
        'success',
      );
    } catch (err: any) {
      showNotification(
        err?.response?.data?.detail || 'Не удалось снять UPI-блокировку',
        'error',
      );
    }
  };

  const handleFilterChange = (field: keyof RequisiteFiltersState, value: unknown) => {
    if (field === 'include_pre_deleted') {
      const next = {
        ...filters,
        include_pre_deleted: Boolean(value),
        page: 1,
      };
      setFilters(next);
      setTotalPages(1);
      loadRequisites(next);
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? (value as number) : 1,
    }));
  };

  const handleSearch = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    setTotalPages(1);
    loadRequisites(next);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setTotalPages(1);
    loadRequisites(defaultFilters);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    const next = { ...filters, page: value };
    setFilters(next);
    loadRequisites(next);
  };

  return {
    requisites,
    traders,
    currencies,
    paymentSystems,
    banks,
    error,
    success,
    loading,
    totalPages,
    filters,
    handleFilterChange,
    handleSearch,
    open,
    setOpen,
    editingRequisite,
    formData,
    setFormData,
    handleOpen,
    handleClose,
    handleSubmit,
    handleDelete,
    handleToggleActive,
    handleUnblock,
    handleResetFilters,
    handlePageChange,
    loadRequisites,
  };
}
